// src/app/dmGecmisi.ts
//
// DM HISTORY MODELİ — "geri tuşu gelen kutusuna döner" (IG paritesi).
//
// ── ÖLÇÜLEN KUSUR ────────────────────────────────────────────────────────
// Sanal dünya (#926) `dm-gonder` eylemini ölçtü:
//     adres `/direct` → `/direct/t/{threadId}` · history yığını **+0**
// Yani sohbet açılışı `replaceState` ile yazılıyordu → sohbetteyken GERİ
// tuşu gelen kutusuna DEĞİL, DM'in tamamen dışına çıkıyordu.
//
// ── AMA ESKİ HÂL BİR UNUTKANLIK DEĞİLDİ ──────────────────────────────────
// `App.tsx:797` birebir şöyle diyordu: *"DM modalı için TEK `/direct`
// history girişi"*, ve `DmInbox.requestClose` `replaceState(…, "/")`
// kullanıyordu, gerekçesi de yazılıydı: *"history.back belirsizliği yok"*.
// Yani eski model **bilinçli bir sadeleştirmeydi** ve bedeli tek bir yerde
// toplanıyordu: kapanışta kaç girdi geri sarılacağını kimsenin bilmesi
// gerekmiyordu, çünkü DM daima 1 girdiydi.
//
// Sohbeti PUSH'a çevirmek o bedeli geri getirir. Bu modül tam olarak o
// bedeli ödemek için var: **"kaç girdi geri" sorusunun cevabını tahmin
// etmek yerine, girdinin KENDİSİNDEN okumak.**
//
// ── ÇEKİRDEK FİKİR: SAYMA, İŞARETLE ──────────────────────────────────────
// `history.go(-n)` için sabit bir `n` seçmek bu depoda ÖLÇÜLMÜŞ bir tuzak:
// DM'e ana sayfadan girince `/direct` PUSH'lanır, permalinkten girince
// (`/c/{id}` üstünde) REPLACE edilir (`App.tsx:563`). Yani DM'in altındaki
// yığın giriş yoluna göre FARKLIDIR ve dışarıdan sayılamaz.
//
// Bu yüzden sayı tutmuyoruz. Sohbet girdisini PUSH ederken `history.state`e
// `{ dmSohbet: true }` işareti koyuyoruz. Kapatan taraf — X, Escape, hatta
// arka-plan tıklaması — üstünde durduğu girdiye BAKIP karar veriyor:
//     işaret varsa → 1 girdi geri sar, sonra kapat
//     işaret yoksa → doğrudan kapat
// İşaret girdinin kendisinde yaşadığı için yeniden-mount, ileri tuşu ve
// derin bağlantı hâllerinde de doğru kalır; bir React ref'i bunların
// hiçbirinde hayatta kalmazdı.
//
// 🧭 KANON — "susturma yollarını say": bu katmanın ÜÇ kapatıcısı var
//    (X düğmesi · Escape/odak hapsi · modal arka planı). Üçü de aynı
//    `dmKatmaniKapat`tan geçmeli; biri bypass ederse geride bayat bir
//    `/direct` girdisi kalır ve GERİ tuşu **DM'i yeniden açar** — yani
//    düzeltme, düzelttiği şeyden daha kötü bir kusur üretir.
//
// ── NEDEN `go(-1)` SONRASI HEMEN YAZMIYORUZ ──────────────────────────────
// `history.go()` ASENKRONDUR. Ardından hemen `replaceState` yazmak yarıştır:
// yazım eski girdiye düşer, sonra traversal gelir ve yazımı ezer. Bu yüzden
// kapanışın ikinci yarısı (`replaceState("/")` + modalı kapat) `popstate`
// olayına BAĞLANIR — zamanlayıcıya değil. Ölçüldü (jsdom, bu depo):
// `go(-1)` popstate'i ateşliyor ve `location.pathname` traversal
// tamamlandığında doğru değerde.

/** `history.state` içine PUSH'lanan sohbet girdisinin işareti. */
export const DM_SOHBET_ISARETI = "dmSohbet";

/** DM katmanının kök yolu — `/direct` gerçekten DM modalını açar. */
export const DM_YOL_DESENI = /^\/direct(?:\/t\/([A-Za-z0-9_-]+))?\/?$/;

/**
 * Sohbetin AÇILDIĞI yol. Üçü de gerçek ve hepsinin history şekli FARKLI:
 *  · `gelenKutusu`   — listeden tıklandı; ALTTA gerçek bir gelen kutusu var.
 *  · `kullanici`     — profil "Mesaj gönder" (`handleStartMessage`); altta
 *                      gelen kutusu YOK (`onBack` zaten `requestClose`).
 *  · `derinBaglanti` — `/direct/t/{id}` soğuk yükü; altta hiç DM girdisi yok.
 */
export type DmSohbetGirisi = "gelenKutusu" | "kullanici" | "derinBaglanti";

/**
 * Sohbet açılışı history'ye YENİ GİRDİ ekler mi?
 *
 * Kural tek cümle: **girdi, ancak ALTINDA geri dönülecek bir ekran varsa
 * eklenir.** Yalnız `gelenKutusu` yolunda öyle bir ekran vardır.
 *
 * ⚠️ `kullanici` yolunda push etmek görünür bir kusur üretirdi: geri tuşu
 * kullanıcıyı HİÇ GÖRMEDİĞİ bir gelen kutusuna bırakırdı. Arayüz zaten
 * bunu söylüyor — `DmInbox` o yolda geri okunu `requestClose`a bağlıyor.
 */
export function dmSohbetAcilisiEkler(giris: DmSohbetGirisi): boolean {
  return giris === "gelenKutusu";
}

/**
 * Bu girdi, BİZİM push ettiğimiz sohbet girdisi mi?
 * (`history.state` her şey olabilir — `null`, string, başka bir modalın
 * yükü. Yalnız birebir `true` kabul edilir.)
 */
export function dmSohbetGirdisiMi(durum: unknown): boolean {
  if (!durum || typeof durum !== "object") return false;
  return (durum as Record<string, unknown>)[DM_SOHBET_ISARETI] === true;
}

/** Katman kapanışında kaç girdi geri sarılmalı: 1 (sohbet push'lu) ya da 0. */
export function dmKapanisGeriAdimi(durum: unknown): number {
  return dmSohbetGirdisiMi(durum) ? 1 : 0;
}

/**
 * Sohbetten gelen kutusuna dönüş (arayüzdeki geri oku) nasıl yapılır?
 *
 *  · `gecmisGeri` — girdiyi biz PUSH ettik → `history.back()`. Girdi
 *    TÜKETİLİR ve senkron `popstate` yolu gelen kutusunu kurar.
 *  · `kokeYaz`    — girdi yok (derin bağlantı) → `/direct`e REPLACE.
 *
 * ⚠️ Push'lu hâlde `replaceState("/direct")` yazmak SESSİZ BİR KUSURDUR:
 * yığın `[…, /direct, /direct]` olur, kullanıcı GERİ'ye basar, URL değişir
 * ama ekran AYNI KALIR — "ölü geri tuşu". Bu yüzden iki dal ayrı.
 */
export function dmGeriOkuKarari(durum: unknown): "gecmisGeri" | "kokeYaz" {
  return dmSohbetGirdisiMi(durum) ? "gecmisGeri" : "kokeYaz";
}

/** `dmKatmaniKapat`ın ne yaptığı — ölçülebilir olsun diye döndürülür. */
export type DmKapanisSonucu =
  /** Geri sarma gerekmedi; kapanış AYNI TURDA tamamlandı. */
  | "hemen"
  /** `go(-1)` çağrıldı; kapanış `popstate` geldiğinde tamamlanacak. */
  | "geriBekleniyor"
  /** `go(-1)` ATILAMADI (history yok/attı) → kapanış yine de tamamlandı. */
  | "geriAtilamadi";

/** `dmKatmaniKapat`ın ihtiyaç duyduğu pencere yüzeyi (test için enjekte edilir). */
export interface DmPencere {
  history: {
    state: unknown;
    go: (delta: number) => void;
  };
  addEventListener: (tip: string, fn: () => void) => void;
  removeEventListener: (tip: string, fn: () => void) => void;
}

/**
 * DM KATMANINI KAPAT — üç kapatıcının da (X · Escape · arka plan) tek yolu.
 *
 * `bitir` = "URL'i `/`e normalleştir + modalı kapat". Bu iş, geri sarma
 * TAMAMLANDIKTAN SONRA yapılmalıdır; aksi halde ya bayat bir `/direct`
 * girdisi kalır (geri tuşu DM'i yeniden açar) ya da yazım traversal'la
 * yarışır.
 *
 * `bitir` EN FAZLA BİR KEZ çağrılır (`bitti` kilidi) — popstate iki kez
 * gelse bile modal iki kez kapanmaz.
 */
export function dmKatmaniKapat(
  bitir: () => void,
  pencere?: DmPencere | null,
): DmKapanisSonucu {
  const w =
    pencere ??
    ((typeof window !== "undefined" ? window : null) as DmPencere | null);
  if (!w || !w.history) {
    bitir();
    return "hemen";
  }

  const adim = dmKapanisGeriAdimi(w.history.state);
  if (adim <= 0) {
    bitir();
    return "hemen";
  }

  let bitti = false;
  const tamamla = () => {
    if (bitti) return;
    bitti = true;
    try {
      w.removeEventListener("popstate", tamamla);
    } catch (_) {
      /* no-op */
    }
    bitir();
  };

  w.addEventListener("popstate", tamamla);
  try {
    w.history.go(-adim);
  } catch (_) {
    // Geri sarma ATILAMADI. Katmanı açık bırakmak en kötü sonuç olurdu
    // (kullanıcı X'e basıyor, hiçbir şey olmuyor) → kapanışı yine de
    // tamamla. Bu dal SESSİZ DEĞİL: dönüş değeri `geriAtilamadi`.
    tamamla();
    return "geriAtilamadi";
  }
  return "geriBekleniyor";
}
